// Typed mirror of the API's nested fabric schema
// (tt-frankford-api/src/models/products/fabrics/fabric.ts).
// Handles (brand.handle, brand.mfr.handle, brand.brand_collection.handle) are
// derived server-side and are therefore optional/read-only on the client.

export type FabricGrade = "a" | "ap" | "b" | "c" | "d" | "e" | "f" | "fp";

export type FabricType = "acrylic" | "vinyl";

export interface IFabricName {
  group?: string;
  color: string;
  display: string;
}

export interface IFabricBrandMfr {
  name: string;
  handle?: string;
}

export interface IFabricBrandCollection {
  name: string;
  handle?: string;
}

export interface IFabricBrand {
  name: string;
  handle?: string;
  mfr: IFabricBrandMfr;
  brand_collection?: IFabricBrandCollection;
}

export interface IFabricUses {
  binding: boolean;
  fringe: boolean;
  underside: boolean;
}

export interface IFabric {
  _id?: string;
  mfr_code: string;
  alt_mfr_code?: string;
  name: IFabricName;
  brand: IFabricBrand;
  frankford_code?: string;
  image?: string;
  // Computed server-side; shown read-only in the UI.
  texture_key?: string;
  // When the swatch/texture bytes last changed on Wasabi. Server-managed. Used as
  // the ?v= cache-bust on CDN image URLs: the keys are deterministic, so without
  // this a re-uploaded or rotated image keeps serving from cache under the same
  // URL. Changes exactly when the image does, so the CDN copy stays cacheable in
  // between — unlike a Date.now() bust, which would defeat caching entirely.
  assets_updated_at?: string;
  grade: FabricGrade;
  pantone?: string;
  fabric_scale: number;
  fabric_type?: FabricType;
  color_group?: string[];
  pattern_group?: string[];
  // Cross-references to other fabrics, stored as ObjectId strings.
  //
  // The API deliberately does NOT populate these ($lookup stages in
  // fabric_service.queryFabrics are commented out, FUMBSD-467) — they arrive as
  // bare ids and the consuming app resolves them itself. Here that means matching
  // against the cached catalog.
  //
  // These are independent of `uses`. A fabric can carry binding comparisons
  // without being flagged for binding, and vice versa: `uses` is an approval, the
  // comparison arrays are reference data, and staff prepare them on their own
  // schedules.
  comparisons?: string[];
  binding_comparisons?: string[];
  fringe_comparisons?: string[];
  // Preserved verbatim on save (not edited here).
  specs?: unknown[];
  retired_names?: string[];
  // Authored lifecycle status — the single field that decides where a fabric is
  // visible. Stores a HANDLE (`coming_soon`), not a display label. The label,
  // note and visibility for that handle live in the fabric_statuses vocabulary
  // (see models/fabric_status.ts), so renaming a label rewrites zero fabrics.
  status?: string;
  // DERIVED SERVER-SIDE from `status` by the API's pre-save hook. Read-only here:
  // sending them has no effect, and editing them would fight the hook. They're
  // kept on the model because the API still returns them (older consumers, like
  // the Shade Designer's "Discontinued" badge, read them).
  discontinued?: boolean;
  coming_soon?: boolean;
  inactive?: boolean;
  // Soft-delete marker (ISO date string). When set, the editor is read-only.
  deleted_at?: string;
  weight?: string;
  uses: IFabricUses;
  // Pricing tier flag ("A-Select"), NOT a visibility flag — a fabric can be
  // select and discontinued at once. Deliberately kept separate from `status`.
  is_select?: boolean;
  // Staff-assigned position within a grade (FUMBDATA-65). Ascending; unset sorts
  // last. Assign in gaps (10, 20, 30…) so fabrics can be slotted in between.
  sort_order?: number;
  index?: number;
  created_at?: string;
  updated_at?: string;
}

// Option lists aligned to the API enums.
export const COLOR_GROUPS: string[] = [
  "Beige", "Black", "Blue", "Brown", "Gold", "Grey", "Green", "Ivory",
  "Orange", "Pink", "Purple", "Red", "Turquoise", "White", "Yellow",
];

export const PATTERN_GROUPS: string[] = [
  "Geometrics", "Solids", "Stripes", "Textures", "Tweeds",
];

export const GRADES: { value: FabricGrade; label: string }[] = [
  { value: "a", label: "Grade A" },
  { value: "ap", label: "Grade A+" },
  { value: "b", label: "Grade B" },
  { value: "c", label: "Grade C" },
  { value: "d", label: "Grade D" },
  { value: "e", label: "Grade E" },
  { value: "f", label: "Grade F" },
  { value: "fp", label: "Grade F+" },
];

export const FABRIC_TYPES: FabricType[] = ["acrylic", "vinyl"];

// Swatch colors for the color-group selector, mirrored from the Shade Designer
// fabric filter palette (tt-shade-designer/src/assets/less/base.less,
// .filter-color-* rules) so the two tools stay visually consistent.
export const COLOR_HEX: Record<string, string> = {
  Beige: "#e6ba8f",
  Black: "#212121",
  Blue: "#107bd6",
  Brown: "#6c3e32",
  Gold: "#eaa91d",
  Grey: "#bcbaba",
  Green: "#057768",
  Ivory: "#e8d4c9",
  Orange: "#fc9034",
  Pink: "#f8818a",
  Purple: "#8c71c3",
  Red: "#ff1a1a",
  Turquoise: "#17b5d0",
  White: "#ffffff",
  Yellow: "#ffd969",
};

// Weight options (oz), matching the Shade Designer weight filter ("8"/"9").
export const WEIGHT_OPTIONS: string[] = ["8", "9"];

/**
 * Fabrics the API refuses to delete, by mfr_code. Mirrors PROTECTED_FABRIC_CODES
 * in tt-frankford-api (models/products/fabrics/helpers.ts) — the API is the one
 * that enforces this; hiding the button here just spares staff a 403 they can do
 * nothing about, and explains why.
 *
 * WHP ("White Poly") is named directly by the SKU pipeline in both directions, so
 * a soft delete would quietly pull it from every customer-facing selector while
 * every historical SKU referencing it stayed valid.
 */
export const PROTECTED_FABRIC_CODES: string[] = ["WHP"];

export function isProtectedFabricCode(mfrCode?: string): boolean {
  if (!mfrCode) return false;
  const code = mfrCode.trim().toUpperCase();
  return PROTECTED_FABRIC_CODES.some((c) => c.toUpperCase() === code);
}

/**
 * The display name staff would otherwise type by hand: "Canvas" + "Navy" →
 * "Canvas Navy". Either part may be absent (Name Group is optional), so this
 * joins whatever is there rather than assuming both.
 */
export function composeDisplayName(name: Partial<IFabricName>): string {
  return [name.group, name.color]
    .map((s) => (s || "").trim())
    .filter(Boolean)
    .join(" ");
}

/**
 * A required field the fabric is currently missing. `key` is the dotted path,
 * which is what the forms match on to paint the offending input red; `label` is
 * what the user is told.
 */
export interface MissingField {
  key: string;
  label: string;
}

/**
 * The fields the API's Fabric schema marks `required` — mirrored here so the form
 * can block a save the server was always going to reject.
 *
 * Keep in step with tt-frankford-api/src/models/products/fabrics/fabric.ts. A
 * field missing from this list isn't a cosmetic slip: it's a 500 on save with a
 * Mongoose validation message the user can't act on.
 *
 * `fabric_scale` is required-with-a-default server-side, so it can only fail here
 * by being blanked out — but blanked out is exactly what an edit can do to it.
 */
export function missingRequiredFields(f: Partial<IFabric>): MissingField[] {
  const blank = (v?: string): boolean => !v || !v.trim();
  const missing: MissingField[] = [];

  if (blank(f.mfr_code)) missing.push({ key: "mfr_code", label: "Manufacturer Code" });
  if (blank(f.name?.color)) missing.push({ key: "name.color", label: "Color Name" });
  if (blank(f.name?.display)) missing.push({ key: "name.display", label: "Display Name" });
  if (blank(f.brand?.mfr?.name)) missing.push({ key: "brand.mfr.name", label: "Manufacturer" });
  if (blank(f.brand?.name)) missing.push({ key: "brand.name", label: "Brand" });
  if (blank(f.grade)) missing.push({ key: "grade", label: "Grade" });
  // Guard the value, not just its presence: `<input type="number">` hands back an
  // empty string (→ NaN under v-model.number) when cleared, and Mongoose would
  // take a 0 quite happily and hand the 3D viewer an unrenderable texture scale.
  if (typeof f.fabric_scale !== "number" || !isFinite(f.fabric_scale) || f.fabric_scale <= 0) {
    missing.push({ key: "fabric_scale", label: "3D Texture Scale" });
  }
  return missing;
}

/** The dotted keys of every currently-missing field, for `v-if`-style lookups. */
export function missingFieldKeys(f: Partial<IFabric>): Record<string, boolean> {
  const map: Record<string, boolean> = {};
  for (const m of missingRequiredFields(f)) map[m.key] = true;
  return map;
}

/**
 * Hyphen/slash-agnostic substring match: fabric codes are written inconsistently
 * ("D-020" vs "D020", "5/615" vs "5615") and staff type them the short way.
 * Normalizing both sides means the search finds the fabric either way.
 */
export function matchesTerm(haystack: string | undefined, term: string): boolean {
  if (!haystack) return false;
  const normalize = (s: string): string => s.toLowerCase().replace(/[-/\s]/g, "");
  return normalize(haystack).indexOf(normalize(term)) !== -1;
}

/**
 * Search the catalog by code, alt code, Frankford code, or display name.
 *
 * Fields are searched in priority order and the results concatenated, then
 * de-duped — so an exact code match outranks an incidental name match. That
 * concatenation doubles as relevance ranking, which is what lets the header's
 * type-ahead show a short, useful top-N.
 *
 * Shared by the home grid's search box and the header's jump-to-fabric field, so
 * typing the same term in either place finds the same fabric in the same order.
 */
export function searchFabrics(fabrics: IFabric[], term: string): IFabric[] {
  const q = term.trim();
  if (!q) return [];

  const byCode = fabrics.filter((f) => matchesTerm(f.mfr_code, q));
  const byAlt = fabrics.filter((f) => matchesTerm(f.alt_mfr_code, q));
  const byFrankford = fabrics.filter((f) => matchesTerm(f.frankford_code, q));
  const byName = fabrics.filter((f) => matchesTerm(f.name?.display, q));

  const seen = new Set<string>();
  const merged: IFabric[] = [];
  for (const f of [...byCode, ...byAlt, ...byFrankford, ...byName]) {
    const id = f._id || f.mfr_code;
    if (!seen.has(id)) {
      seen.add(id);
      merged.push(f);
    }
  }
  return merged;
}

// Returns a blank fabric for the Add view.
export function emptyFabric(): IFabric {
  return {
    mfr_code: "",
    alt_mfr_code: "",
    name: { group: "", color: "", display: "" },
    brand: {
      name: "",
      mfr: { name: "" },
      brand_collection: { name: "" },
    },
    frankford_code: "",
    image: "",
    grade: "" as FabricGrade,
    pantone: "",
    fabric_scale: 1,
    fabric_type: "acrylic",
    color_group: [],
    pattern_group: [],
    comparisons: [],
    binding_comparisons: [],
    fringe_comparisons: [],
    retired_names: [],
    // New fabrics are staff-only until someone deliberately activates them —
    // this is the "seeded early, images not ready yet" case. The API defaults to
    // the same value; set it here too so the form shows the truth up front.
    status: "draft",
    weight: "",
    uses: { binding: false, fringe: false, underside: false },
    is_select: false,
  };
}
